/**
 * Helpers for placing direction arrows along a deck.gl PathLayer.
 * Used by `useCachedDirectionsLayers` so any map flow can show driving-direction
 * arrows on top of a Routes-API polyline.
 */

// Cap arrow count so cross-country routes can't drown the GPU even when the
// caller asks for a tiny interval.
const MAX_ARROWS_ALONG_PATH = 400
const DEFAULT_ARROW_INTERVAL_METERS = 60
const ARROW_ICON_SIZE = 16
const ICON_CACHE = new Map()

// Equirectangular distance in metres — fast and accurate enough for spacing
// arrows over driving distances. Avoids the cost of full Haversine.
const distanceMeters = (a, b) => {
  const RAD = Math.PI / 180
  const R = 6371000
  const dLat = (b[1] - a[1]) * RAD
  const dLng = (b[0] - a[0]) * RAD
  const meanLat = ((a[1] + b[1]) / 2) * RAD
  const x = dLng * Math.cos(meanLat)
  return R * Math.sqrt(x * x + dLat * dLat)
}

/**
 * Returns a cached canvas-rendered arrow icon, keyed by colour so re-renders
 * reuse the same image. The returned shape includes `id` (deck.gl uses it to
 * key its internal icon-atlas cache) and `mask: false` so the icon renders
 * with its baked-in colour rather than getColor.
 *
 * @param {string} colorHex - fill color for the arrow
 * @returns {{ id: string, url: string, width: number, height: number, anchorX: number, anchorY: number, mask: boolean }}
 */
export const buildArrowIcon = colorHex => {
  const cached = ICON_CACHE.get(colorHex)
  if (cached) return cached

  // Render at 2× the displayed size so deck.gl's downscale stays crisp.
  const renderSize = ARROW_ICON_SIZE * 2
  const canvas = document.createElement('canvas')
  canvas.width = renderSize
  canvas.height = renderSize
  const ctx = canvas.getContext('2d')

  ctx.beginPath()
  ctx.moveTo(renderSize * 0.5, renderSize * 0.15)
  ctx.lineTo(renderSize * 0.88, renderSize * 0.85)
  ctx.lineTo(renderSize * 0.5, renderSize * 0.6)
  ctx.lineTo(renderSize * 0.12, renderSize * 0.85)
  ctx.closePath()
  ctx.fillStyle = colorHex
  ctx.fill()

  const icon = {
    id: `cached-directions-arrow-${colorHex}`,
    url: canvas.toDataURL('image/png'),
    width: renderSize,
    height: renderSize,
    anchorX: renderSize / 2,
    anchorY: renderSize / 2,
    mask: false,
  }
  ICON_CACHE.set(colorHex, icon)
  return icon
}

/**
 * Samples a decoded polyline at a constant geographic interval, interpolating
 * between vertices so arrow spacing stays even regardless of how the Routes
 * API densified the path. The caller supplies the desired interval (typically
 * derived from current zoom); the function still floors it at
 * `totalLengthMeters / MAX_ARROWS_ALONG_PATH` so the GPU never sees more than
 * `MAX_ARROWS_ALONG_PATH` icons.
 *
 * @param {[number, number][]} path - decoded polyline (lng/lat pairs)
 * @param {number} [intervalMeters=DEFAULT_ARROW_INTERVAL_METERS]
 * @returns {{ position: [number, number], angle: number }[]}
 *   Each arrow's angle is in deck.gl convention (degrees, 0 = up/north, clockwise positive).
 */
export const buildArrowsAlongPath = (path, intervalMeters = DEFAULT_ARROW_INTERVAL_METERS) => {
  if (!Array.isArray(path) || path.length < 2) return []

  const segments = []
  let totalLength = 0
  for (let i = 0; i < path.length - 1; i += 1) {
    const len = distanceMeters(path[i], path[i + 1])
    if (len > 0) {
      segments.push({ a: path[i], b: path[i + 1], len })
      totalLength += len
    }
  }
  if (totalLength === 0) return []

  const interval = Math.max(intervalMeters, totalLength / MAX_ARROWS_ALONG_PATH)
  const arrows = []
  let cumulative = 0
  let nextThreshold = interval / 2
  segments.forEach(seg => {
    const segEnd = cumulative + seg.len
    while (nextThreshold <= segEnd) {
      const t = (nextThreshold - cumulative) / seg.len
      arrows.push({
        position: [seg.a[0] + t * (seg.b[0] - seg.a[0]), seg.a[1] + t * (seg.b[1] - seg.a[1])],
        angle: Math.atan2(seg.b[0] - seg.a[0], seg.b[1] - seg.a[1]) * (180 / Math.PI),
      })
      nextThreshold += interval
    }
    cumulative = segEnd
  })
  return arrows
}
